import { ServicePointGroupWithRefs } from 'src/app/public/service-point-groups/service-point-group.types'
import { FilterParams } from '../../utilities/http-params'
import {
  ActiveFilterDisplay,
  QueryParam,
} from './common'
import { FilterAction } from './filter.service'

export interface ServicePointSearchFilterState {
  value: string | null,
  config: {
    meta: {
      fieldName: string,
      fieldGroup: string,
      fieldDisplay: string,
    },
    constant?: boolean
  }
}

export const initialServicePointSearchFilterState: ServicePointSearchFilterState = {
  value: null,
  config: {
    meta: {
      fieldName: '',
      fieldGroup: '',
      fieldDisplay: '',
    },
  },
}

export interface SetServicePointSearchFilterConfigAction {
  type: 'SET_SERVICE_POINT_SEARCH_FILTER_CONFIG_ACTION',
  payload: Pick<ServicePointSearchFilterState, 'config'>,
  meta: Omit<ServicePointSearchFilterState['config']['meta'], 'fieldDisplay'>
}

export interface SetServicePointSearchFilterValueAction {
  type: 'SET_SERVICE_POINT_SEARCH_FILTER_VALUE_ACTION',
  payload: Pick<ServicePointSearchFilterState, 'value'>,
  meta: Omit<ServicePointSearchFilterState['config']['meta'], 'fieldDisplay'>
}

export type ServicePointSearchFilterAction =
  | SetServicePointSearchFilterConfigAction
  | SetServicePointSearchFilterValueAction

export const servicePointSearchFilterReducer = (state: ServicePointSearchFilterState, action: ServicePointSearchFilterAction) => {
  switch (action.type) {
    case 'SET_SERVICE_POINT_SEARCH_FILTER_CONFIG_ACTION':
      return {
        ...state,
        config: action.payload.config,
      }
    case 'SET_SERVICE_POINT_SEARCH_FILTER_VALUE_ACTION':
      return {
        ...state,
        value: action.payload.value,
      }
    default:
      return state
  }
}

export class ServicePointSearchFilter {
  public readonly type = 'SERVICE_POINT_SEARCH'

  public static SetServicePointSearchFilterConfigAction(
    payload: SetServicePointSearchFilterConfigAction['payload'],
    meta: Omit<ServicePointSearchFilterState['config']['meta'], 'fieldDisplay'>,
  ): SetServicePointSearchFilterConfigAction { return { type: 'SET_SERVICE_POINT_SEARCH_FILTER_CONFIG_ACTION', payload, meta } }
  public static SetServicePointSearchFilterValueAction(
    payload: SetServicePointSearchFilterValueAction['payload'],
    meta: Omit<ServicePointSearchFilterState['config']['meta'], 'fieldDisplay'>,
  ): SetServicePointSearchFilterValueAction { return { type: 'SET_SERVICE_POINT_SEARCH_FILTER_VALUE_ACTION', payload, meta } }


  public state: ServicePointSearchFilterState
  constructor(
    partialStateWithConfig: Partial<ServicePointSearchFilterState> & Pick<ServicePointSearchFilterState, 'config'>,
    private dispatch: (action: FilterAction) => void,
  ) {
    this.state = { ...initialServicePointSearchFilterState, ...partialStateWithConfig }
  }

  public apply(action: ServicePointSearchFilterAction) {
    this.state = servicePointSearchFilterReducer(this.state, action)
    return this
  }

  public asParams(): FilterParams[] {
    return this.state.value !== null ? [
      {
        name: this.state.config.meta.fieldName,
        values: [
          {
            value: this.state.value,
          },
        ],
      },
    ] : []
  }

  public asActiveDisplay(): ActiveFilterDisplay[] {
    return this.state.value !== null
      ? [
        {
          headerName: this.state.config.meta.fieldDisplay,
          operation: 'like',
          value: `"${this.state.value}"`,
          constant: this.state.config.constant ?? false,
          remove: (() =>
            this.dispatch(ServicePointSearchFilter.SetServicePointSearchFilterValueAction(
              { value: null },
              this.state.config.meta,
            ))
          ),
        },
      ]
      : []
  }

  public asCount() {
    return this.state.value !== null ? 1 : 0
  }

  public fromUrlQueryParams(
    params: readonly QueryParam[],
    defaultVal: string | null | undefined,
  ) {
    const qp = params.find((p) => p.name === this.state.config.meta.fieldName)
    if (qp?.value) {
      this.dispatch(ServicePointSearchFilter.SetServicePointSearchFilterValueAction(
        { value: qp.value },
        this.state.config.meta,
      ))
    }
    else if (defaultVal) {
      this.dispatch(ServicePointSearchFilter.SetServicePointSearchFilterValueAction(
        { value: defaultVal },
        this.state.config.meta,
      ))
    }
  }

  public asUrlQueryParams(): QueryParam[] {
    return this.state.value !== null
      ? [
        {
          name: this.state.config.meta.fieldName,
          value: this.state.value,
        },
      ]
      : []
  }

  public asDashboardQueryParams(): QueryParam[] {
    return this.state.value !== null
      ? [
        {
          name: this.state.config.meta.fieldName,
          value: this.state.value,
        },
      ]
      : []
  }

  asServicePointGroupWithRefs(): Partial<ServicePointGroupWithRefs> {
    const allowed_fields = [
      'service_point_id_search',
    ]
    const field = this.state.config.meta.fieldName
    return allowed_fields.includes(field)
      ? { [field]: this.state.value }
      : {}
  }
}
